import React, { useEffect, useState } from "react";
import {
  Container,
  TextField,
  Button,
  Typography,
  Box,
  InputAdornment,
  IconButton,
  CircularProgress,
  LinearProgress,
  Modal,
  Fade,
  Backdrop
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useNavigate } from "react-router-dom";

const BRAND = "#003366";
const BRAND_HOVER = "#00264d";

const UpdatePasswordPage = () => {
  const [userData, setUserData] = useState({
    nombre: "",
    telefono: "",
    email: ""
  });

  const [loading, setLoading] = useState(true);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [strength, setStrength] = useState(0);
  const [open, setOpen] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    fetch("/sistema/get-user-info/", { credentials: "include" })
      .then((r) => r.json())
      .then((d) => {
        setUserData({
          nombre: d.nombre || "",
          telefono: d.telefono || "",
          email: d.email || ""
        });
        setLoading(false);
      })
      .catch(() => {
        setError("Error al cargar datos");
        setLoading(false);
      });
  }, []);

  const getCsrf = async () => {
    const r = await fetch("/sistema/get-csrf-token/", {
      credentials: "include"
    });

    const d = await r.json();
    return d.csrfToken;
  };

  const validar = (v) => {
    let s = 0;

    if (v.length >= 8) s++;
    if (/[A-Z]/.test(v)) s++;
    if (/[a-z]/.test(v)) s++;
    if (/[0-9]/.test(v)) s++;
    if (/[^A-Za-z0-9]/.test(v)) s++;

    setStrength(s);

    return s >= 3;
  };

  const guardar = async () => {
    setError("");

    if (!userData.nombre || !userData.telefono) {
      setError("Completa tus datos");
      return;
    }

    if (password !== confirm) {
      setError("Las contraseñas no coinciden");
      return;
    }

    if (!validar(password)) {
      setError("Contraseña débil");
      return;
    }

    const csrf = await getCsrf();

    if (!csrf) {
      setError("CSRF inválido");
      return;
    }

    try {
      const res = await fetch("/sistema/update-password/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrf
        },
        credentials: "include",
        body: JSON.stringify({
          email: userData.email,
          nombre: userData.nombre,
          telefono: userData.telefono,
          password: password
        })
      });

      const data = await res.json();

      if (res.ok) {
        setOpen(true);
      } else {
        setError(data.error || "Error");
      }
    } catch {
      setError("Servidor no responde");
    }
  };

  const color = () => {
    if (strength <= 1) return "red";
    if (strength === 2) return "orange";
    if (strength === 3) return "yellow";
    if (strength === 4) return "lightgreen";
    return "green";
  };

  return (
    <Container maxWidth="sm" sx={{ py: 6 }}>

      <Typography fontWeight={900} fontSize="2rem" color={BRAND}>
        Completa tu registro
      </Typography>

      <Typography sx={{ mb: 3 }}>
        Revisa tus datos y crea tu contraseña definitiva
      </Typography>

      {loading ? (
        <CircularProgress />
      ) : (
        <Box display="flex" flexDirection="column" gap={2}>

          {error && (
            <Typography color="error">{error}</Typography>
          )}

          <TextField
            label="Correo"
            value={userData.email}
            disabled
            fullWidth
          />

          <TextField
            label="Nombre"
            value={userData.nombre}
            onChange={(e) =>
              setUserData({ ...userData, nombre: e.target.value })
            }
            fullWidth
          />

          <TextField
            label="Teléfono"
            value={userData.telefono}
            onChange={(e) =>
              setUserData({ ...userData, telefono: e.target.value })
            }
            fullWidth
          />

          <TextField
            label="Nueva contraseña"
            type={show ? "text" : "password"}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              validar(e.target.value);
            }}
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShow(!show)}>
                    {show ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />

          <LinearProgress
            variant="determinate"
            value={(strength / 5) * 100}
            sx={{
              height: 8,
              borderRadius: 3,
              "& .MuiLinearProgress-bar": {
                backgroundColor: color()
              }
            }}
          />

          <TextField
            label="Confirmar contraseña"
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            fullWidth
          />

          <Button
            onClick={guardar}
            sx={{
              mt: 2,
              height: 52,
              fontWeight: 900,
              color: "#fff",
              backgroundColor: BRAND,
              "&:hover": {
                backgroundColor: BRAND_HOVER
              }
            }}
          >
            GUARDAR Y CONTINUAR
          </Button>

        </Box>
      )}

      <Modal
        open={open}
        closeAfterTransition
        BackdropComponent={Backdrop}
      >
        <Fade in={open}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%,-50%)",
              bgcolor: "#fff",
              borderRadius: 3,
              p: 4,
              width: "90%",
              maxWidth: 420,
              textAlign: "center"
            }}
          >
            <Typography fontWeight={900} mb={1}>
              Registro completado
            </Typography>

            <Typography mb={3}>
              Ya puedes iniciar sesión
            </Typography>

            <Button
              onClick={() => navigate("/login")}
              sx={{
                fontWeight: 900,
                backgroundColor: BRAND,
                color: "#fff",
                "&:hover": {
                  backgroundColor: BRAND_HOVER
                }
              }}
            >
              IR AL LOGIN
            </Button>
          </Box>
        </Fade>
      </Modal>

    </Container>
  );
};

export default UpdatePasswordPage;
